/** Person memberships use the same catalogue and refuse stale whole-list replacement. */
import { ApiError } from '@/api/client';
import { useSkillWrites, useSkills } from '@/api/skills';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Button, Panel } from '@/ui';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

type Props = { userId: number; mayEdit: boolean; title?: ReactNode };
export function FerdigheterPanel(props: Props) {
  return <PersonSkills key={props.userId} {...props} />;
}
function PersonSkills({ userId, mayEdit, title }: { userId: number; mayEdit: boolean; title?: ReactNode }) {
  const q = useSkills();
  const w = useSkillWrites();
  const can = useCan();
  const [sp] = useSearchParams();
  const fromSkills = sp.get('fra') === 'ferdigheter';
  const sourceSkill = /^\d+$/.test(sp.get('ferdighet') ?? '') ? sp.get('ferdighet') : null;
  const anchor = useRef<HTMLDivElement>(null);
  const focused = useRef(false);
  const lock = useRef(false);
  const [problem, setProblem] = useState('');
  const [status, setStatus] = useState('');
  const [attempt, setAttempt] = useState<{
    skills: number[];
    expected_skills: number[];
  } | null>(null);
  const [conflict, setConflict] = useState(false);
  const skills = Array.isArray(q.data) ? q.data : [];
  const mine = skills.filter((k) => k.people.includes(userId)).map((k) => k.id);
  const mayRead = can('vaktplan.se') || can('innstillinger.endre') || can('ansatte.endre');
  useEffect(() => {
    if (fromSkills && q.isSuccess && !focused.current) {
      anchor.current?.scrollIntoView({ block: 'center' });
      anchor.current?.focus({ preventScroll: true });
      focused.current = true;
    }
  }, [fromSkills, q.isSuccess]);
  const set = async (body: { skills: number[]; expected_skills: number[] }) => {
    if (lock.current) return;
    lock.current = true;
    setAttempt(body);
    setProblem('');
    setConflict(false);
    setStatus(T('Lagrer …'));
    try {
      await w.setPerson.mutateAsync({ userId, ...body });
      setAttempt(null);
      setStatus(T('Ferdighetene er lagret.'));
    } catch (e) {
      setProblem(e instanceof Error ? e.message : T('Kunne ikke lagre ferdighetene. Prøv igjen.'));
      setConflict(e instanceof ApiError && e.code === 'skills_changed');
      setStatus('');
    } finally {
      lock.current = false;
    }
  };
  const refresh = async () => {
    const result = await q.refetch();
    if (result.isSuccess) {
      setProblem('');
      setConflict(false);
      setAttempt(null);
      setStatus(T('Siste versjon er hentet. Se gjennom valgene.'));
    }
  };
  if (!mayRead) return null;
  return (
    <div id="ferdigheter" ref={anchor} tabIndex={-1} className="person-skills-anchor">
      <Panel
        item
        title={title ?? T('Ferdigheter')}
        sub={
          mayEdit
            ? T('Velg kompetansen denne ansatte har. Endringer lagres automatisk.')
            : T('Kompetansen denne ansatte har.')
        }
      >
        {fromSkills && (
          <Link
            className="person-skills-back"
            to={`/innstillinger/ferdigheter${sourceSkill ? `#ferdighet-${sourceSkill}` : ''}`}
          >
            {T('Tilbake til ferdigheter')}
          </Link>
        )}
        {q.isPending ? (
          <output>{T('Henter ferdigheter …')}</output>
        ) : q.isError ? (
          <div role="alert">
            <p>{T('Kunne ikke hente siste versjon av ferdighetene. Prøv igjen for å se gjeldende valg.')}</p>
            <Button onClick={() => void refresh()}>{T('Prøv igjen')}</Button>
          </div>
        ) : skills.length ? (
          <>
            <fieldset
              className="skillset"
              disabled={!mayEdit || w.setPerson.isPending || !!problem}
              data-testid="person-skills"
            >
              <legend className="sr-only">{T('Ferdigheter')}</legend>
              {skills.map((k) => (
                <label key={k.id} className={String(k.id) === sourceSkill ? 'skillset-requested' : undefined}>
                  <input
                    type="checkbox"
                    checked={mine.includes(k.id)}
                    onChange={(e) =>
                      void set({
                        skills: e.target.checked ? [...mine, k.id] : mine.filter((id) => id !== k.id),
                        expected_skills: mine,
                      })
                    }
                  />
                  {k.name}
                </label>
              ))}
            </fieldset>
            <p className="person-skills-status">
              {mayEdit
                ? T('Vaktplan oppdateres når endringen er lagret.')
                : T('Du har lesetilgang til ferdighetene.')}
            </p>
          </>
        ) : (
          <p className="hint">
            {T('Bedriften har ingen ferdigheter ennå.')}{' '}
            {can('innstillinger.endre') ? (
              <Link to="/innstillinger/ferdigheter">{T('Lag dem i Innstillinger')}</Link>
            ) : (
              T('Be den som administrerer driftsinnstillingene om å legge dem til.')
            )}
          </p>
        )}
        {status && <output className="person-skills-status">{status}</output>}
        {problem && (
          <div className="person-skills-error">
            <p role="alert">{problem}</p>
            <div>
              {attempt && !conflict && (
                <Button disabled={w.setPerson.isPending} onClick={() => void set(attempt)}>
                  {T('Prøv å lagre igjen')}
                </Button>
              )}
              <Button disabled={w.setPerson.isPending} onClick={() => void refresh()}>
                {T('Hent siste versjon')}
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
